import { UserButton } from "@clerk/clerk-react";
import { Moon, Sun } from "lucide-react";
import { useCallback, useSyncExternalStore } from "react";
import { resolveAppUrl } from "../../lib/url";
import { ConnectionStatus } from "../shared/ConnectionStatus";

const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

declare global {
  interface Window {
    __CLAWDBOT_ASSISTANT_NAME__?: string;
    __CLAWDBOT_ASSISTANT_AVATAR__?: string;
  }
}

// ---- theme store (dark/light) ----

function getThemeSnapshot(): "dark" | "light" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function subscribeTheme(cb: () => void): () => void {
  const observer = new MutationObserver(cb);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}

function toggleTheme() {
  const next = document.documentElement.classList.toggle("dark") ? "dark" : "light";
  localStorage.setItem("theme", next);
}

// ---- component ----

export function Header() {
  const theme = useSyncExternalStore(subscribeTheme, getThemeSnapshot);
  const orgName = window.__CLAWDBOT_ASSISTANT_NAME__ ?? "LexPortal";
  const onToggle = useCallback(() => toggleTheme(), []);

  return (
    <header className="flex h-12 items-center justify-between border-b border-border bg-panel/60 px-4 backdrop-blur">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground">{orgName}</span>
        <ConnectionStatus />
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggle}
          className="rounded-md p-1.5 text-muted transition-colors hover:bg-accent hover:text-foreground"
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        {clerkPk && (
          <UserButton
            afterSignOutUrl={resolveAppUrl("/sign-in")}
            appearance={{ elements: { avatarBox: "h-7 w-7" } }}
          />
        )}
      </div>
    </header>
  );
}
